import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import { Card, CardContent } from '@repo/ui/components/ui/card'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import { ScrollArea } from '@repo/ui/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/ui/select'
import { Switch } from '@repo/ui/components/ui/switch'
import { surfaceRadii, surfaceSpacing } from '@repo/ui/lib/design-tokens'
import { cn } from '@repo/ui/lib/utils'
import { Check, ChevronDown, Pencil, Plus, Trash2, X } from 'lucide-react'
import {
  type ComponentPropsWithoutRef,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'
import { AppTopBarActionGroup, AppTopBarActions } from '@/components/app/top-bar-actions'
import {
  AppWorkspacePane,
  AppWorkspacePaneHeader,
  AppWorkspacePanel,
  AppWorkspaceSidebarDetailSplit,
} from '@/components/app/workspace-shell'
import { FORM, LAYOUT, PREVIEW, RADIUS, SPACING, SURFACE } from '@/lib/design/onboarding'
import type { OperatorConfigurationState, ServiceAreaRecord } from '@/lib/operator-config/types'
import { trpc } from '@/lib/trpc'
import { type AddressValue, parsePostalAddress } from './location-shared'

const ServiceAreaMap = lazy(() =>
  import('@/components/operator/service-area-map').then((module) => ({
    default: module.ServiceAreaMap,
  }))
)

const NEW_AREA_ID = '__new__'
const areaCardSurfaceClass = cn(
  'overflow-hidden border shadow-none transition-colors',
  surfaceRadii.xl
)
const areaCardActiveClass =
  'border-primary/25 bg-primary/[0.045] shadow-[0_18px_50px_-38px_color-mix(in_oklab,var(--primary)_55%,transparent)]'
const areaCardIdleClass = 'border-border/70 bg-background/78 hover:bg-background'
const noExtendedServiceValue = '__none__'
const coverageRadiusPresets = [10, 20, 30] as const
const roundedBadgeClass = RADIUS.pill

type ZctaCoverageGeometry =
  | {
      type: 'Polygon'
      coordinates: number[][][]
    }
  | {
      type: 'MultiPolygon'
      coordinates: number[][][][]
    }

type ZctaCoverageCandidate = {
  postalCode: string
  overlapPercent: number
  geometry: ZctaCoverageGeometry
}

const EMPTY_ZCTA_CANDIDATES: ZctaCoverageCandidate[] = []

export interface CoveragePreviewDraft {
  previewArea: ServiceAreaRecord
  selectedAreaId: string | null
  emptyStateLabel: string
}
const summaryBadgeClass = cn(
  surfaceRadii.full,
  FORM.badge,
  'border-border/70 bg-muted/45 font-normal text-muted-foreground'
)
const iconButtonClass = cn(
  surfaceRadii.full,
  'border-border/70 bg-background text-muted-foreground shadow-none hover:text-foreground'
)
const selectFieldClass =
  'h-11 w-full rounded-[4px] border border-input bg-background px-3 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50'
const decoratedInputClass = FORM.input
const inputAdornmentClass =
  'pointer-events-none absolute inset-y-0 flex items-center text-sm font-medium text-muted-foreground'
const extendedCoverageOptions = [
  { value: noExtendedServiceValue, label: 'None' },
  { value: 'flat', label: 'Flat fee' },
  { value: 'percent', label: 'Percentage' },
  { value: 'per_mile', label: 'Per mile' },
] as const

function normalizeZipCode(value: string) {
  const trimmed = value.trim()
  if (/^\d{5}(?:-\d{4})?$/.test(trimmed)) {
    return trimmed
  }

  const match = trimmed.match(/\b\d{5}(?:-\d{4})?\b/)
  return match?.[0] ?? ''
}

function parsePostalCodeList(value: string | null | undefined) {
  if (!value) return []

  try {
    const parsed = JSON.parse(value)
    if (!Array.isArray(parsed)) return []
    return [...new Set(parsed.filter((item): item is string => /^\d{5}$/.test(item)))].sort()
  } catch {
    return []
  }
}

function toAddressValue(area: ServiceAreaRecord | null): AddressValue {
  const addressText = area?.centerAddressText ?? ''

  return {
    addressText: normalizeZipCode(addressText) || addressText,
    lat: area?.centerLat ?? undefined,
    lng: area?.centerLng ?? undefined,
    h3Index: area?.centerH3Index ?? undefined,
  }
}

function createDraftName(configuration: OperatorConfigurationState) {
  return `Area ${configuration.serviceAreaPolicies.length + 1}`
}

function fallbackAreaName(address: AddressValue, fallback: string) {
  const directZipCode = normalizeZipCode(address.addressText)
  if (directZipCode) return directZipCode

  const postalCode = parsePostalAddress(address).postalCode.trim()
  if (postalCode) return postalCode

  const text = address.addressText.trim()
  return text || fallback
}

function isResolvedAddress(address: AddressValue) {
  return Boolean(address.lat && address.lng && address.h3Index)
}

function getAreaHydrationKey(area: ServiceAreaRecord | null) {
  if (!area) return 'none'

  return [
    area.id,
    area.name,
    area.centerAddressText,
    area.centerLat,
    area.centerLng,
    area.centerH3Index,
    area.softRadiusMiles,
    area.hardRadiusMiles,
    area.surchargeType,
    area.surchargeAmount,
    area.isDefault,
    area.includedPostalCodesJson,
    area.excludedPostalCodesJson,
  ].join(':')
}

function ZipCodeField({
  value,
  onChange,
  onboardingMode = false,
}: {
  value: AddressValue
  onChange: (next: AddressValue) => void
  onboardingMode?: boolean
}) {
  const [query, setQuery] = useState(value.addressText)
  const [debouncedQuery, setDebouncedQuery] = useState(value.addressText)
  const [browserCoords, setBrowserCoords] = useState<{ lat: number; lng: number } | null>(null)
  const autoDetectionRequestedRef = useRef(false)
  const autoDetectionAppliedRef = useRef(false)

  useEffect(() => {
    setQuery(value.addressText)
  }, [value.addressText])

  useEffect(() => {
    if (!onboardingMode || autoDetectionRequestedRef.current || value.addressText.trim()) {
      return
    }

    if (!(typeof navigator !== 'undefined' && 'geolocation' in navigator)) {
      return
    }

    autoDetectionRequestedRef.current = true

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setBrowserCoords({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        })
      },
      () => {},
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 300000,
      }
    )
  }, [onboardingMode, value.addressText])

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(query), 250)
    return () => window.clearTimeout(timeout)
  }, [query])

  const normalizedZipCode = normalizeZipCode(debouncedQuery)
  const hasResolvedAddress = Boolean(value.lat && value.lng && value.h3Index)
  const viewerLocationQuery = trpc.geo.viewerLocation.useQuery(undefined, {
    enabled: onboardingMode,
  })
  const autoDetectionCoords = useMemo(() => {
    if (!onboardingMode || hasResolvedAddress || query.trim() || autoDetectionAppliedRef.current) {
      return null
    }

    if (browserCoords) {
      return browserCoords
    }

    if (viewerLocationQuery.data) {
      return {
        lat: viewerLocationQuery.data.lat,
        lng: viewerLocationQuery.data.lng,
      }
    }

    return null
  }, [browserCoords, hasResolvedAddress, onboardingMode, query, viewerLocationQuery.data])
  const reverseGeocodeQuery = trpc.geo.reverseGeocode.useQuery(
    {
      lat: autoDetectionCoords?.lat ?? 0,
      lng: autoDetectionCoords?.lng ?? 0,
    },
    {
      enabled: Boolean(autoDetectionCoords),
      retry: false,
    }
  )
  const geocodeQuery = trpc.geo.geocode.useQuery(
    { address: normalizedZipCode },
    {
      enabled:
        Boolean(normalizedZipCode) &&
        normalizedZipCode === debouncedQuery.trim() &&
        !(value.lat && value.lng && value.h3Index),
      retry: false,
    }
  )

  useEffect(() => {
    if (!reverseGeocodeQuery.data?.postalCode || autoDetectionAppliedRef.current || query.trim())
      return

    autoDetectionAppliedRef.current = true
    setQuery(reverseGeocodeQuery.data.postalCode)
    onChange({
      addressText: reverseGeocodeQuery.data.postalCode,
      lat: String(reverseGeocodeQuery.data.lat),
      lng: String(reverseGeocodeQuery.data.lng),
      h3Index: reverseGeocodeQuery.data.h3Index,
    })
  }, [onChange, query, reverseGeocodeQuery.data])

  useEffect(() => {
    if (!geocodeQuery.data || !normalizedZipCode) return

    onChange({
      addressText: normalizedZipCode,
      lat: String(geocodeQuery.data.lat),
      lng: String(geocodeQuery.data.lng),
      h3Index: geocodeQuery.data.h3Index,
    })
  }, [geocodeQuery.data, normalizedZipCode, onChange])

  const isCheckingZipCode = geocodeQuery.isFetching || reverseGeocodeQuery.isFetching

  return (
    <div className={FORM.fieldStack}>
      <Label>Service area center ZIP code</Label>
      <Input
        value={query}
        placeholder="94103"
        className={cn(
          decoratedInputClass,
          'font-medium tracking-[0.08em] uppercase shadow-none focus-visible:ring-2'
        )}
        inputMode="numeric"
        autoComplete="postal-code"
        onChange={(event) => {
          const nextZipCode = event.target.value
          setQuery(nextZipCode)
          onChange({
            addressText: nextZipCode,
            lat: undefined,
            lng: undefined,
            h3Index: undefined,
          })
        }}
      />
      {isCheckingZipCode ? (
        <p className={FORM.fieldHint}>
          {reverseGeocodeQuery.isFetching ? 'Detecting ZIP code…' : 'Checking ZIP code…'}
        </p>
      ) : null}
      {geocodeQuery.error && normalizedZipCode === debouncedQuery.trim() ? (
        <p className="text-xs text-destructive">Enter a valid ZIP code.</p>
      ) : !isCheckingZipCode && !hasResolvedAddress ? (
        <p className={FORM.fieldHint}>
          {onboardingMode
            ? 'Center of your coverage.'
            : 'We’ll use this ZIP code as the center of your service area.'}
        </p>
      ) : null}
    </div>
  )
}

function resolveAreaName(name: string, address: AddressValue, fallback: string) {
  const trimmed = name.trim()
  return trimmed || fallbackAreaName(address, fallback)
}

function formatFeeSummary(area: {
  surchargeType: ServiceAreaRecord['surchargeType']
  surchargeAmount: number | null
}) {
  if (!area.surchargeType || area.surchargeAmount == null) {
    return 'No extended area'
  }

  if (area.surchargeType === 'percent') {
    return `${area.surchargeAmount / 100}% surcharge`
  }

  if (area.surchargeType === 'per_mile') {
    return `$${(area.surchargeAmount / 100).toFixed(2)} / mile`
  }

  return `$${(area.surchargeAmount / 100).toFixed(2)} flat`
}

function formatFeeAmountForDisplay(feeType: string, rawValue: string) {
  if (!rawValue) return ''

  const numericValue = Number(rawValue)
  if (!Number.isFinite(numericValue)) return rawValue

  const displayValue = feeType ? numericValue / 100 : numericValue
  return displayValue.toFixed(2).replace(/\.?0+$/, '')
}

function parseFeeAmountFromDisplay(value: string) {
  if (!value.trim()) return ''

  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return value

  return String(Math.round(numericValue * 100))
}

function DecoratedInput({
  prefix,
  suffix,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Input> & {
  prefix?: string
  suffix?: string
}) {
  return (
    <div className="relative">
      {prefix ? <span className={cn(inputAdornmentClass, 'left-3')}>{prefix}</span> : null}
      <Input
        className={cn(decoratedInputClass, prefix ? 'pl-8' : '', suffix ? 'pr-11' : '', className)}
        {...props}
      />
      {suffix ? <span className={cn(inputAdornmentClass, 'right-3')}>{suffix}</span> : null}
    </div>
  )
}

export function CoveragePreviewCard({
  configuration,
  draft,
  className,
  mapClassName,
  showSummary = true,
  showFrame = true,
}: {
  configuration: OperatorConfigurationState
  draft?: CoveragePreviewDraft | null
  className?: string
  mapClassName?: string
  showSummary?: boolean
  showFrame?: boolean
}) {
  const viewerLocationQuery = trpc.geo.viewerLocation.useQuery()
  const previewArea = draft?.previewArea ?? configuration.serviceArea ?? null
  const selectedAreaId = draft?.selectedAreaId ?? configuration.serviceArea?.id ?? null
  const includedMiles = previewArea?.softRadiusMiles ?? 15
  const maxMiles = previewArea?.hardRadiusMiles ?? includedMiles
  const hasExtendedArea = Boolean(previewArea?.surchargeType)
  const previewCenter = useMemo<[number, number] | undefined>(() => {
    if (previewArea?.centerLat && previewArea.centerLng) {
      return [Number(previewArea.centerLat), Number(previewArea.centerLng)]
    }

    if (viewerLocationQuery.data) {
      return [viewerLocationQuery.data.lat, viewerLocationQuery.data.lng]
    }

    return undefined
  }, [previewArea?.centerLat, previewArea?.centerLng, viewerLocationQuery.data])
  const viewportSignal = previewCenter
    ? `${selectedAreaId ?? 'draft'}:${previewCenter[0]}:${previewCenter[1]}`
    : (selectedAreaId ?? undefined)
  const draftPolicy = draft ? draft.previewArea : null
  const mapAreas = useMemo(() => {
    if (!draftPolicy) {
      return configuration.serviceAreaPolicies
    }

    if (selectedAreaId === NEW_AREA_ID) {
      return [...configuration.serviceAreaPolicies, draftPolicy]
    }

    if (!selectedAreaId) {
      return configuration.serviceAreaPolicies.length > 0
        ? configuration.serviceAreaPolicies
        : [draftPolicy]
    }

    return configuration.serviceAreaPolicies.some((area) => area.id === selectedAreaId)
      ? configuration.serviceAreaPolicies.map((area) =>
          area.id === selectedAreaId ? draftPolicy : area
        )
      : [...configuration.serviceAreaPolicies, draftPolicy]
  }, [configuration.serviceAreaPolicies, draftPolicy, selectedAreaId])
  const emptyMapMessage =
    draft?.emptyStateLabel ??
    (previewArea?.centerAddressText?.trim()
      ? `We’re locating ${normalizeZipCode(previewArea.centerAddressText) || previewArea.centerAddressText.trim()} on the map.`
      : '')

  return (
    <section
      className={cn(
        showFrame ? PREVIEW.map.shell : 'h-full min-h-[26rem]',
        showFrame ? surfaceRadii.xl : '',
        className
      )}
    >
      <div className={cn(showFrame ? PREVIEW.map.stage : 'h-full')}>
        <section className={PREVIEW.map.frame}>
          {showSummary ? (
            <div className={PREVIEW.map.summaryBar}>
              <span className="font-medium text-foreground">Coverage preview</span>
              <span className={PREVIEW.map.summaryBadgePrimary}>Included · {includedMiles} mi</span>
              {hasExtendedArea ? (
                <span className={PREVIEW.map.summaryBadgeMuted}>Extended · {maxMiles} mi</span>
              ) : null}
            </div>
          ) : null}
          <Suspense fallback={<div className={PREVIEW.map.loading}>Loading map preview…</div>}>
            <ServiceAreaMap
              policies={mapAreas}
              selectedPolicyId={selectedAreaId}
              draftPolicy={selectedAreaId === NEW_AREA_ID ? draftPolicy : null}
              places={configuration.locations}
              defaultCenter={previewCenter}
              className={cn(PREVIEW.map.canvas, mapClassName)}
              viewportSignal={viewportSignal}
              emptyStateLabel={emptyMapMessage}
            />
          </Suspense>
        </section>
      </div>
    </section>
  )
}

function loadAreaIntoEditor(
  area: ServiceAreaRecord | null,
  configuration: OperatorConfigurationState,
  setSelectedAreaId: (value: string) => void,
  setAreaName: (value: string) => void,
  setCenterAddress: (value: AddressValue) => void,
  setSoftRadiusMiles: (value: string) => void,
  setHardRadiusMiles: (value: string) => void,
  setFeeType: (value: string) => void,
  setFeeAmount: (value: string) => void,
  setIsDefault: (value: boolean) => void,
  setIsEditingName: (value: boolean) => void
) {
  setIsEditingName(false)

  if (!area) {
    setSelectedAreaId(NEW_AREA_ID)
    setAreaName(createDraftName(configuration))
    setCenterAddress({ addressText: '' })
    setSoftRadiusMiles('15')
    setHardRadiusMiles('25')
    setFeeType('')
    setFeeAmount('')
    setIsDefault(configuration.serviceAreaPolicies.length === 0)
    return
  }

  setSelectedAreaId(area.id)
  setAreaName(area.name)
  setCenterAddress(toAddressValue(area))
  setSoftRadiusMiles(String(area.softRadiusMiles))
  setHardRadiusMiles(String(area.hardRadiusMiles))
  setFeeType(area.surchargeType ?? '')
  setFeeAmount(area.surchargeAmount != null ? String(area.surchargeAmount) : '')
  setIsDefault(area.isDefault)
}

export function BookingAreaEditor({
  configuration,
  onSaved,
  createRequestKey = 0,
  singleAreaOnly = false,
  hidePrimarySubmit = false,
  onboardingMode = false,
  showInlinePreview = true,
  onRegisterSubmit,
  onPreviewChange,
}: {
  configuration: OperatorConfigurationState
  onSaved?: () => Promise<void> | void
  createRequestKey?: number
  singleAreaOnly?: boolean
  hidePrimarySubmit?: boolean
  onboardingMode?: boolean
  showInlinePreview?: boolean
  onRegisterSubmit?: (submit: () => Promise<boolean>) => void
  onPreviewChange?: (draft: CoveragePreviewDraft | null) => void
}) {
  const saveArea = trpc.serviceArea.set.useMutation()
  const deleteArea = trpc.serviceArea.delete.useMutation()
  const viewerLocationQuery = trpc.geo.viewerLocation.useQuery()

  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(
    singleAreaOnly ? (configuration.serviceArea?.id ?? NEW_AREA_ID) : null
  )
  const [focusedAreaId, setFocusedAreaId] = useState<string | null>(
    configuration.serviceArea?.id ?? configuration.serviceAreaPolicies[0]?.id ?? null
  )
  const [areaName, setAreaName] = useState(
    configuration.serviceArea?.name ?? createDraftName(configuration)
  )
  const [centerAddress, setCenterAddress] = useState<AddressValue>(
    toAddressValue(configuration.serviceArea)
  )
  const [lastResolvedCenterAddress, setLastResolvedCenterAddress] = useState<AddressValue>(
    toAddressValue(configuration.serviceArea)
  )
  const [softRadiusMiles, setSoftRadiusMiles] = useState(
    String(configuration.serviceArea?.softRadiusMiles ?? 15)
  )
  const [hardRadiusMiles, setHardRadiusMiles] = useState(
    String(configuration.serviceArea?.hardRadiusMiles ?? 25)
  )
  const [feeType, setFeeType] = useState(configuration.serviceArea?.surchargeType ?? '')
  const [feeAmount, setFeeAmount] = useState(
    configuration.serviceArea?.surchargeAmount != null
      ? String(configuration.serviceArea.surchargeAmount)
      : ''
  )
  const [isDefault, setIsDefault] = useState(Boolean(configuration.serviceArea?.isDefault))
  const [isEditingName, setIsEditingName] = useState(false)
  const [nameInputValue, setNameInputValue] = useState(
    configuration.serviceArea?.name ?? createDraftName(configuration)
  )
  const [excludedPostalCodes, setExcludedPostalCodes] = useState<string[]>(
    parsePostalCodeList(configuration.serviceArea?.excludedPostalCodesJson)
  )
  const hydratedAreaKeyRef = useRef<string | null>(null)

  const selectedSavedArea =
    configuration.serviceAreaPolicies.find((area) => area.id === selectedAreaId) ?? null
  const focusedSavedArea =
    selectedSavedArea ??
    configuration.serviceAreaPolicies.find((area) => area.id === focusedAreaId) ??
    configuration.serviceArea ??
    configuration.serviceAreaPolicies[0] ??
    null

  useEffect(() => {
    setFocusedAreaId((current) => {
      if (current && configuration.serviceAreaPolicies.some((area) => area.id === current)) {
        return current
      }

      return configuration.serviceArea?.id ?? configuration.serviceAreaPolicies[0]?.id ?? null
    })
  }, [configuration.serviceArea?.id, configuration.serviceAreaPolicies])

  useEffect(() => {
    if (singleAreaOnly) {
      const nextArea = configuration.serviceArea ?? configuration.serviceAreaPolicies[0] ?? null
      const nextKey = `single:${getAreaHydrationKey(nextArea)}`
      if (hydratedAreaKeyRef.current === nextKey) return
      hydratedAreaKeyRef.current = nextKey

      loadAreaIntoEditor(
        nextArea,
        configuration,
        setSelectedAreaId,
        setAreaName,
        setCenterAddress,
        setSoftRadiusMiles,
        setHardRadiusMiles,
        setFeeType,
        setFeeAmount,
        setIsDefault,
        setIsEditingName
      )
      setLastResolvedCenterAddress(toAddressValue(nextArea))
      setExcludedPostalCodes(parsePostalCodeList(nextArea?.excludedPostalCodesJson))
      return
    }

    if (selectedAreaId == null) {
      hydratedAreaKeyRef.current = null
      setIsEditingName(false)
      return
    }

    if (selectedAreaId === NEW_AREA_ID) {
      if (configuration.serviceAreaPolicies.length === 0) {
        const nextKey = 'new:empty'
        if (hydratedAreaKeyRef.current === nextKey) return
        hydratedAreaKeyRef.current = nextKey

        loadAreaIntoEditor(
          null,
          configuration,
          setSelectedAreaId,
          setAreaName,
          setCenterAddress,
          setSoftRadiusMiles,
          setHardRadiusMiles,
          setFeeType,
          setFeeAmount,
          setIsDefault,
          setIsEditingName
        )
        setLastResolvedCenterAddress({ addressText: '' })
        setExcludedPostalCodes([])
      }
      return
    }

    const nextArea =
      configuration.serviceAreaPolicies.find((area) => area.id === selectedAreaId) ??
      configuration.serviceArea ??
      configuration.serviceAreaPolicies[0] ??
      null

    const nextKey = `selected:${selectedAreaId}:${getAreaHydrationKey(nextArea)}`
    if (hydratedAreaKeyRef.current === nextKey) return
    hydratedAreaKeyRef.current = nextKey

    loadAreaIntoEditor(
      nextArea,
      configuration,
      setSelectedAreaId,
      setAreaName,
      setCenterAddress,
      setSoftRadiusMiles,
      setHardRadiusMiles,
      setFeeType,
      setFeeAmount,
      setIsDefault,
      setIsEditingName
    )
    setLastResolvedCenterAddress(toAddressValue(nextArea))
    setExcludedPostalCodes(parsePostalCodeList(nextArea?.excludedPostalCodesJson))
  }, [configuration, selectedAreaId, singleAreaOnly])

  useEffect(() => {
    if (selectedAreaId || !focusedSavedArea) return
    setExcludedPostalCodes(parsePostalCodeList(focusedSavedArea.excludedPostalCodesJson))
  }, [focusedSavedArea, selectedAreaId])

  useEffect(() => {
    if (createRequestKey > 0) {
      loadAreaIntoEditor(
        null,
        configuration,
        setSelectedAreaId,
        setAreaName,
        setCenterAddress,
        setSoftRadiusMiles,
        setHardRadiusMiles,
        setFeeType,
        setFeeAmount,
        setIsDefault,
        setIsEditingName
      )
      setLastResolvedCenterAddress({ addressText: '' })
      setExcludedPostalCodes([])
    }
  }, [configuration, createRequestKey])

  useEffect(() => {
    if (!isEditingName) {
      setNameInputValue(areaName)
    }
  }, [areaName, isEditingName])

  const includedMiles = Math.max(Number(softRadiusMiles) || 0, 1)
  const maxMiles = Math.max(Number(hardRadiusMiles) || 0, includedMiles)
  const isCreatingNewArea = selectedAreaId === NEW_AREA_ID
  const previewAddress = isResolvedAddress(centerAddress)
    ? centerAddress
    : lastResolvedCenterAddress
  const mapAddress = selectedAreaId ? previewAddress : toAddressValue(focusedSavedArea)
  const mapRadiusMiles = selectedAreaId
    ? includedMiles
    : (focusedSavedArea?.softRadiusMiles ?? includedMiles)
  const zctaCoverageQuery = trpc.serviceArea.previewZctaCoverage.useQuery(
    {
      centerLat: Number(mapAddress.lat ?? 0),
      centerLng: Number(mapAddress.lng ?? 0),
      radiusMiles: mapRadiusMiles,
      overlapThreshold: 0.4,
    },
    {
      enabled: Boolean(mapAddress.lat && mapAddress.lng),
      retry: false,
      staleTime: 1000 * 60 * 10,
    }
  )
  const zctaCandidates = (zctaCoverageQuery.data ??
    EMPTY_ZCTA_CANDIDATES) as ZctaCoverageCandidate[]
  const generatedPostalCodes = useMemo(
    () => zctaCandidates.map((candidate) => candidate.postalCode),
    [zctaCandidates]
  )
  const includedPostalCodes = useMemo(
    () => generatedPostalCodes.filter((postalCode) => !excludedPostalCodes.includes(postalCode)),
    [excludedPostalCodes, generatedPostalCodes]
  )
  const visibleZctaCoverage = useMemo(
    () =>
      zctaCandidates.map((candidate) => ({
        ...candidate,
        excluded: excludedPostalCodes.includes(candidate.postalCode),
      })),
    [excludedPostalCodes, zctaCandidates]
  )

  const draftArea = useMemo(() => {
    const trimmedName = resolveAreaName(areaName, centerAddress, createDraftName(configuration))

    return {
      id: selectedSavedArea?.id ?? NEW_AREA_ID,
      operatorId: selectedSavedArea?.operatorId ?? configuration.operator?.id ?? 'draft',
      name: trimmedName,
      isDefault,
      centerAddressText: centerAddress.addressText || null,
      centerLat: centerAddress.lat ?? null,
      centerLng: centerAddress.lng ?? null,
      centerH3Index: centerAddress.h3Index ?? null,
      softRadiusMiles: includedMiles,
      hardRadiusMiles: feeType ? maxMiles : includedMiles,
      includedPostalCodesJson: includedPostalCodes.length
        ? JSON.stringify(includedPostalCodes)
        : (selectedSavedArea?.includedPostalCodesJson ?? null),
      excludedPostalCodesJson: excludedPostalCodes.length
        ? JSON.stringify(excludedPostalCodes)
        : null,
      coverageSource: includedPostalCodes.length
        ? 'census_tigerweb_zcta_2020'
        : (selectedSavedArea?.coverageSource ?? null),
      coverageUpdatedAt: selectedSavedArea?.coverageUpdatedAt ?? null,
      surchargeType: feeType ? (feeType as ServiceAreaRecord['surchargeType']) : null,
      surchargeAmount: feeType && feeAmount ? Number(feeAmount) : null,
      maxDriveTimeBetweenMinutes: selectedSavedArea?.maxDriveTimeBetweenMinutes ?? null,
    } satisfies ServiceAreaRecord
  }, [
    areaName,
    centerAddress,
    configuration,
    feeAmount,
    feeType,
    includedPostalCodes,
    includedMiles,
    isDefault,
    maxMiles,
    excludedPostalCodes,
    selectedSavedArea?.coverageSource,
    selectedSavedArea?.coverageUpdatedAt,
    selectedSavedArea?.includedPostalCodesJson,
    selectedSavedArea?.maxDriveTimeBetweenMinutes,
    selectedSavedArea?.id,
    selectedSavedArea?.operatorId,
  ])

  const previewDraftArea = useMemo(
    () => ({
      ...draftArea,
      centerAddressText: previewAddress.addressText || null,
      centerLat: previewAddress.lat ?? null,
      centerLng: previewAddress.lng ?? null,
      centerH3Index: previewAddress.h3Index ?? null,
      zctaCoverage: visibleZctaCoverage,
    }),
    [draftArea, previewAddress, visibleZctaCoverage]
  )

  const mapPreviewArea = useMemo(() => {
    if (selectedAreaId) return previewDraftArea
    if (!focusedSavedArea) return null

    return {
      ...focusedSavedArea,
      zctaCoverage: visibleZctaCoverage,
    }
  }, [focusedSavedArea, previewDraftArea, selectedAreaId, visibleZctaCoverage])

  const mapAreas = useMemo(() => {
    if (!mapPreviewArea) return configuration.serviceAreaPolicies
    if (mapPreviewArea.id === NEW_AREA_ID)
      return [...configuration.serviceAreaPolicies, mapPreviewArea]

    return configuration.serviceAreaPolicies.map((area) =>
      area.id === mapPreviewArea.id ? mapPreviewArea : area
    )
  }, [configuration.serviceAreaPolicies, mapPreviewArea])

  const displayAreaName = resolveAreaName(
    selectedAreaId ? areaName : (focusedSavedArea?.name ?? ''),
    selectedAreaId ? centerAddress : toAddressValue(focusedSavedArea),
    isCreatingNewArea ? createDraftName(configuration) : 'Area'
  )
  const previewCenter = useMemo<[number, number] | undefined>(() => {
    if (mapAddress.lat && mapAddress.lng) {
      return [Number(mapAddress.lat), Number(mapAddress.lng)]
    }

    if (viewerLocationQuery.data) {
      return [viewerLocationQuery.data.lat, viewerLocationQuery.data.lng]
    }

    return undefined
  }, [mapAddress.lat, mapAddress.lng, viewerLocationQuery.data])
  const viewportSignal = previewCenter
    ? `${mapPreviewArea?.id ?? 'draft'}:${previewCenter[0]}:${previewCenter[1]}`
    : (mapPreviewArea?.id ?? undefined)
  const emptyMapMessage = mapAddress.addressText.trim()
    ? zctaCoverageQuery.isFetching
      ? 'Loading coverage boundaries…'
      : 'Coverage boundaries are unavailable for this ZIP code.'
    : 'Enter a ZIP code to center your coverage map.'

  const handleCenterAddressChange = useCallback((next: AddressValue) => {
    setCenterAddress(next)

    if (isResolvedAddress(next)) {
      setLastResolvedCenterAddress(next)
    }
  }, [])

  const handleTogglePostalCode = useCallback((postalCode: string) => {
    setExcludedPostalCodes((current) => {
      const isExcluded = current.includes(postalCode)
      return isExcluded
        ? current.filter((currentPostalCode) => currentPostalCode !== postalCode)
        : [...current, postalCode].sort()
    })
  }, [])

  const handleSaveArea = useCallback(async () => {
    if (!centerAddress.lat || !centerAddress.lng || !centerAddress.h3Index) {
      toast.error('Choose a valid ZIP code for this area.')
      return false
    }

    if (feeType && maxMiles <= includedMiles) {
      toast.error('Extended service area must be farther than the service area.')
      return false
    }

    if (feeType && !feeAmount) {
      toast.error('Add pricing for the extended service area.')
      return false
    }

    if (zctaCoverageQuery.isError) {
      toast.error('Coverage boundaries are unavailable for this ZIP code.')
      return false
    }

    try {
      const saved = await saveArea.mutateAsync({
        id: selectedSavedArea?.id,
        name: resolveAreaName(areaName, centerAddress, createDraftName(configuration)),
        isDefault,
        centerAddressText: centerAddress.addressText || undefined,
        centerLat: centerAddress.lat,
        centerLng: centerAddress.lng,
        centerH3Index: centerAddress.h3Index,
        softRadiusMiles: includedMiles,
        hardRadiusMiles: feeType ? maxMiles : includedMiles,
        surchargeType: feeType ? (feeType as 'flat' | 'percent' | 'per_mile') : null,
        surchargeAmount: feeType && feeAmount ? Number(feeAmount) : null,
        maxDriveTimeBetweenMinutes: selectedSavedArea?.maxDriveTimeBetweenMinutes ?? null,
        includedPostalCodes: generatedPostalCodes.length > 0 ? includedPostalCodes : undefined,
        excludedPostalCodes:
          generatedPostalCodes.length > 0
            ? excludedPostalCodes.filter((postalCode) => generatedPostalCodes.includes(postalCode))
            : undefined,
      })

      setSelectedAreaId(saved.id)
      await onSaved?.()
      toast.success(selectedSavedArea ? 'Booking area updated.' : 'Booking area added.')
      return true
    } catch {
      toast.error('Could not save booking area.')
      return false
    }
  }, [
    areaName,
    centerAddress,
    configuration,
    feeAmount,
    feeType,
    generatedPostalCodes,
    includedPostalCodes,
    includedMiles,
    isDefault,
    maxMiles,
    onSaved,
    saveArea,
    excludedPostalCodes,
    selectedSavedArea,
    zctaCoverageQuery.isError,
  ])

  useEffect(() => {
    onRegisterSubmit?.(handleSaveArea)
  }, [handleSaveArea, onRegisterSubmit])

  useEffect(() => {
    onPreviewChange?.(
      singleAreaOnly
        ? {
            previewArea: previewDraftArea,
            selectedAreaId,
            emptyStateLabel: emptyMapMessage,
          }
        : null
    )
  }, [emptyMapMessage, onPreviewChange, previewDraftArea, selectedAreaId, singleAreaOnly])

  async function handleDeleteArea() {
    if (!selectedSavedArea) return

    await deleteArea.mutateAsync({ id: selectedSavedArea.id })
    await onSaved?.()
    setSelectedAreaId(null)
    toast.success('Booking area removed.')
  }

  function startEditingName() {
    setNameInputValue(areaName)
    setIsEditingName(true)
  }

  function confirmNameEdit() {
    setAreaName(nameInputValue)
    setIsEditingName(false)
  }

  function cancelNameEdit() {
    setNameInputValue(areaName)
    setIsEditingName(false)
  }

  function renderExpandedHeader(kind: 'new' | 'existing', hideDismiss = false) {
    const isExisting = kind === 'existing'

    if (singleAreaOnly) {
      return null
    }

    return (
      <div className={cn(SPACING.section.sm, LAYOUT.rows.split)}>
        <div className="min-w-0 flex-1">
          <div className={LAYOUT.rows.splitCenter}>
            <div className={FORM.rows.tags}>
              {isEditingName ? (
                <Input
                  value={nameInputValue}
                  onChange={(event) => setNameInputValue(event.target.value)}
                  placeholder={kind === 'new' ? 'New area' : 'Area name'}
                  className="h-8 w-[11rem] border-0 bg-transparent px-0 py-0 text-[15px] font-semibold shadow-none focus-visible:ring-0 sm:w-[13rem]"
                  autoFocus
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      confirmNameEdit()
                    }
                    if (event.key === 'Escape') {
                      cancelNameEdit()
                    }
                  }}
                />
              ) : (
                <div className="truncate text-[15px] font-semibold text-foreground">
                  {displayAreaName}
                </div>
              )}

              {isEditingName ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    className={iconButtonClass}
                    onClick={confirmNameEdit}
                  >
                    <Check className="h-3.5 w-3.5" />
                    <span className="sr-only">Save area name</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    className={iconButtonClass}
                    onClick={cancelNameEdit}
                  >
                    <X className="h-3.5 w-3.5" />
                    <span className="sr-only">Cancel area name edit</span>
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  className={iconButtonClass}
                  onClick={startEditingName}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  <span className="sr-only">Edit area name</span>
                </Button>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {kind === 'new' ? (
                <Badge variant="secondary" className={roundedBadgeClass}>
                  New
                </Badge>
              ) : null}
              {isExisting ? (
                <Badge variant="outline" className={roundedBadgeClass}>
                  Editing
                </Badge>
              ) : null}
              {kind === 'existing' && isDefault ? (
                <Badge variant="secondary" className={roundedBadgeClass}>
                  Default
                </Badge>
              ) : null}
            </div>
          </div>
        </div>
        {hideDismiss ? null : isExisting ? (
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            className={cn(iconButtonClass, 'mt-0.5 size-8 shrink-0')}
            onClick={() => {
              setIsEditingName(false)
              setSelectedAreaId(null)
            }}
          >
            <ChevronDown className="h-4 w-4" />
            <span className="sr-only">Collapse area</span>
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            className={cn(iconButtonClass, 'mt-0.5 size-8 shrink-0')}
            onClick={() => {
              setIsEditingName(false)
              setSelectedAreaId(null)
            }}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Cancel new area</span>
          </Button>
        )}
      </div>
    )
  }

  function renderAreaEditor(showDelete: boolean) {
    const primaryActionLabel = singleAreaOnly
      ? selectedSavedArea
        ? 'Save service area'
        : 'Create service area'
      : showDelete
        ? 'Save changes'
        : 'Create area'
    const hasExtendedArea = Boolean(feeType)
    const amountLabel =
      feeType === 'percent' ? 'Markup' : feeType === 'per_mile' ? 'Price per mile' : 'Flat fee'
    const amountPrefix = feeType === 'percent' ? undefined : '$'
    const amountSuffix = feeType === 'percent' ? '%' : feeType === 'per_mile' ? '/mi' : undefined
    const amountPlaceholder =
      feeType === 'percent' ? '12.5' : feeType === 'per_mile' ? '2.50' : '25'
    const handleFeeTypeChange = (nextValue: string) => {
      const nextFeeType = nextValue === noExtendedServiceValue ? '' : nextValue
      setFeeType(nextFeeType)
      if (!nextFeeType) {
        setFeeAmount('')
        setHardRadiusMiles(softRadiusMiles)
      }
    }

    return (
      <div
        className={cn(
          onboardingMode && singleAreaOnly
            ? FORM.stepContent
            : `${SPACING.stack.lg} ${SPACING.section.sm} md:px-5`,
          singleAreaOnly ? '' : 'border-t border-border/70'
        )}
      >
        <div className={LAYOUT.grid.weighted}>
          <ZipCodeField
            value={centerAddress}
            onChange={handleCenterAddressChange}
            onboardingMode={onboardingMode && singleAreaOnly}
          />
          <div className={FORM.fieldStack}>
            <Label>{singleAreaOnly ? 'Included radius' : 'Service area radius'}</Label>
            <DecoratedInput
              type="number"
              min={1}
              max={100}
              value={softRadiusMiles}
              inputMode="numeric"
              placeholder="15"
              suffix="mi"
              onChange={(event) => setSoftRadiusMiles(event.target.value)}
            />
            {onboardingMode && singleAreaOnly ? (
              <div className={FORM.rows.badge}>
                {coverageRadiusPresets.map((preset) => {
                  const isActive = includedMiles === preset
                  return (
                    <Button
                      key={preset}
                      type="button"
                      variant={isActive ? 'default' : 'outline'}
                      size="sm"
                      className={cn(RADIUS.pill, 'px-3', !isActive ? 'bg-background/80' : '')}
                      onClick={() => setSoftRadiusMiles(String(preset))}
                    >
                      {preset} mi
                    </Button>
                  )
                })}
              </div>
            ) : null}
            <p className={FORM.fieldHint}>
              {onboardingMode && singleAreaOnly
                ? 'Standard travel.'
                : 'Customers inside this radius can book at your standard travel rate.'}
            </p>
          </div>
        </div>

        <div
          className={cn(
            onboardingMode && singleAreaOnly
              ? 'grid gap-3 border-y border-border/45 py-3 md:grid-cols-[minmax(0,1fr)_minmax(12rem,0.75fr)] md:items-end [&>*:nth-child(3)]:md:col-span-2'
              : SURFACE.insetSection
          )}
        >
          <div className={FORM.fieldStack}>
            <p className="text-sm font-semibold text-foreground">
              {onboardingMode && singleAreaOnly ? 'Extra travel' : 'Optional extended coverage'}
            </p>
            <p className={cn(FORM.fieldHint, 'leading-5')}>
              {onboardingMode && singleAreaOnly
                ? 'Let customers book a little farther out.'
                : 'Use this only if you want to accept jobs outside your standard radius for an added fee.'}
            </p>
          </div>

          <div className={FORM.fieldStack}>
            <Label>{onboardingMode && singleAreaOnly ? 'Fee' : 'Extended coverage option'}</Label>
            {onboardingMode && singleAreaOnly ? (
              <fieldset className={FORM.rows.badge}>
                <legend className="sr-only">Extended coverage fee</legend>
                {extendedCoverageOptions.map((option) => {
                  const isActive = (feeType || noExtendedServiceValue) === option.value
                  return (
                    <Button
                      key={option.value}
                      type="button"
                      variant={isActive ? 'default' : 'outline'}
                      size="sm"
                      className={cn(RADIUS.pill, 'px-3', !isActive ? 'bg-background/80' : '')}
                      aria-pressed={isActive}
                      onClick={() => handleFeeTypeChange(option.value)}
                    >
                      {option.label}
                    </Button>
                  )
                })}
              </fieldset>
            ) : (
              <Select value={feeType || noExtendedServiceValue} onValueChange={handleFeeTypeChange}>
                <SelectTrigger className={cn(selectFieldClass, FORM.selectTrigger)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {extendedCoverageOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {onboardingMode && singleAreaOnly ? null : (
              <p className={FORM.fieldHint}>
                Optional: allow bookings beyond your standard radius with an added fee.
              </p>
            )}
          </div>

          {hasExtendedArea ? (
            <div className={LAYOUT.grid.twoColumn}>
              <div className={FORM.fieldStack}>
                <Label>{amountLabel}</Label>
                <DecoratedInput
                  type="number"
                  min={0}
                  step={feeType === 'percent' ? '0.1' : '0.01'}
                  inputMode="decimal"
                  value={formatFeeAmountForDisplay(feeType, feeAmount)}
                  onChange={(event) => setFeeAmount(parseFeeAmountFromDisplay(event.target.value))}
                  placeholder={amountPlaceholder}
                  prefix={amountPrefix}
                  suffix={amountSuffix}
                />
              </div>
              <div className={FORM.fieldStack}>
                <Label>Extended radius</Label>
                <DecoratedInput
                  type="number"
                  min={includedMiles + 1}
                  max={100}
                  value={hardRadiusMiles}
                  inputMode="numeric"
                  placeholder="25"
                  suffix="mi"
                  onChange={(event) => setHardRadiusMiles(event.target.value)}
                />
                <p className={FORM.fieldHint}>
                  {onboardingMode && singleAreaOnly
                    ? 'Only used when extra travel is on.'
                    : 'This outer ring is only available when extended coverage is enabled.'}
                </p>
              </div>
            </div>
          ) : null}
        </div>

        {!singleAreaOnly ? (
          <div className={FORM.fieldStack}>
            <Card className={SURFACE.subtleCard}>
              <CardContent className={cn(SPACING.section.sm, LAYOUT.rows.splitCenter)}>
                <div>
                  <div className="text-sm font-medium">Default area</div>
                  <p className={FORM.fieldHint}>Used for scheduling.</p>
                </div>
                <Switch checked={isDefault} onCheckedChange={setIsDefault} />
              </CardContent>
            </Card>
          </div>
        ) : null}

        <div className={cn(LAYOUT.actionRow, showDelete ? 'justify-between' : 'justify-end')}>
          {showDelete ? (
            <Button
              type="button"
              variant="outline"
              className={RADIUS.pill}
              onClick={() => void handleDeleteArea()}
              disabled={deleteArea.isPending}
            >
              <Trash2 className="h-4 w-4" />
              Delete area
            </Button>
          ) : null}
          {!hidePrimarySubmit && (singleAreaOnly || onboardingMode) ? (
            <Button
              type="button"
              className={RADIUS.pill}
              onClick={() => void handleSaveArea()}
              disabled={saveArea.isPending}
            >
              {saveArea.isPending ? 'Saving…' : primaryActionLabel}
            </Button>
          ) : null}
        </div>
      </div>
    )
  }

  if (singleAreaOnly) {
    if (!showInlinePreview) {
      return <section className={SURFACE.editorSection}>{renderAreaEditor(false)}</section>
    }

    return (
      <div className={cn(LAYOUT.grid.coverageSplit, onboardingMode ? 'items-start' : '')}>
        <section className={SURFACE.editorSection}>{renderAreaEditor(false)}</section>

        <CoveragePreviewCard
          configuration={configuration}
          draft={{
            previewArea: previewDraftArea,
            selectedAreaId,
            emptyStateLabel: emptyMapMessage,
          }}
        />
      </div>
    )
  }

  return (
    <>
      <AppTopBarActions>
        <AppTopBarActionGroup>
          {selectedAreaId ? (
            <Button
              type="button"
              size="sm"
              className="cursor-pointer gap-2"
              onClick={() => void handleSaveArea()}
              disabled={saveArea.isPending}
            >
              {saveArea.isPending ? 'Saving…' : isCreatingNewArea ? 'Create area' : 'Save changes'}
            </Button>
          ) : null}
        </AppTopBarActionGroup>
      </AppTopBarActions>
      <AppWorkspacePanel>
        <AppWorkspaceSidebarDetailSplit>
          <AppWorkspacePane as="aside" surface="region" className="relative flex flex-col">
            <AppWorkspacePaneHeader
              eyebrow="Service areas"
              description={`${configuration.serviceAreaPolicies.length} saved · ${
                configuration.serviceAreaPolicies.filter((area) => area.isDefault).length
              } default`}
              actions={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 shrink-0 cursor-pointer gap-2"
                  onClick={() =>
                    loadAreaIntoEditor(
                      null,
                      configuration,
                      setSelectedAreaId,
                      setAreaName,
                      setCenterAddress,
                      setSoftRadiusMiles,
                      setHardRadiusMiles,
                      setFeeType,
                      setFeeAmount,
                      setIsDefault,
                      setIsEditingName
                    )
                  }
                >
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              }
            />
            <ScrollArea className="min-h-0 flex-1">
              <div className={cn(surfaceSpacing.stack.compact, surfaceSpacing.inset.compact)}>
                {configuration.serviceAreaPolicies.length === 0 && !isCreatingNewArea ? (
                  <div className="space-y-3">
                    <div className="app-empty-surface p-4 text-sm text-muted-foreground">
                      No service areas saved yet.
                    </div>
                    <Button
                      className="w-full cursor-pointer gap-2"
                      onClick={() =>
                        loadAreaIntoEditor(
                          null,
                          configuration,
                          setSelectedAreaId,
                          setAreaName,
                          setCenterAddress,
                          setSoftRadiusMiles,
                          setHardRadiusMiles,
                          setFeeType,
                          setFeeAmount,
                          setIsDefault,
                          setIsEditingName
                        )
                      }
                    >
                      <Plus className="h-4 w-4" />
                      Add first area
                    </Button>
                  </div>
                ) : null}

                {!singleAreaOnly && isCreatingNewArea ? (
                  <Card className={cn(areaCardSurfaceClass, areaCardActiveClass, 'gap-0 py-0')}>
                    {renderExpandedHeader('new')}
                    {renderAreaEditor(false)}
                  </Card>
                ) : null}

                {!singleAreaOnly &&
                  configuration.serviceAreaPolicies.map((area) => {
                    const isExpanded = selectedAreaId === area.id
                    const isFocused = !isExpanded && focusedSavedArea?.id === area.id

                    return (
                      <Card
                        key={area.id}
                        className={cn(
                          areaCardSurfaceClass,
                          'gap-0 py-0',
                          isExpanded || isFocused ? areaCardActiveClass : areaCardIdleClass
                        )}
                      >
                        {isExpanded ? (
                          renderExpandedHeader('existing')
                        ) : (
                          <div
                            className={cn(
                              SPACING.section.sm,
                              'flex w-full items-start gap-3 text-left'
                            )}
                          >
                            <button
                              type="button"
                              className="flex min-w-0 flex-1 cursor-pointer items-start gap-4 text-left"
                              onClick={() => setFocusedAreaId(area.id)}
                            >
                              <div className="mt-0.5 flex flex-col items-center">
                                <div className="h-2.5 w-2.5 rounded-full bg-primary/70" />
                                <div className="mt-1 h-12 w-px bg-border/80" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className={LAYOUT.rows.splitCenter}>
                                  <div className="min-w-0">
                                    <div className={FORM.rows.tags}>
                                      <div className="truncate text-[15px] font-semibold text-foreground">
                                        {resolveAreaName(area.name, toAddressValue(area), 'Area')}
                                      </div>
                                      {area.isDefault ? (
                                        <Badge variant="secondary" className={roundedBadgeClass}>
                                          Default
                                        </Badge>
                                      ) : null}
                                    </div>
                                    {area.centerAddressText ? (
                                      <div className="mt-1 text-sm text-muted-foreground">
                                        {area.centerAddressText}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                                <div className={FORM.rows.badge}>
                                  <Badge variant="outline" className={summaryBadgeClass}>
                                    Service {area.softRadiusMiles} mi
                                  </Badge>
                                  {area.surchargeType ? (
                                    <Badge variant="outline" className={summaryBadgeClass}>
                                      Extended {area.hardRadiusMiles} mi
                                    </Badge>
                                  ) : null}
                                  <Badge variant="outline" className={summaryBadgeClass}>
                                    {formatFeeSummary(area)}
                                  </Badge>
                                </div>
                              </div>
                            </button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon-sm"
                              className={cn(iconButtonClass, 'size-8 shrink-0')}
                              onClick={() => {
                                setFocusedAreaId(area.id)
                                loadAreaIntoEditor(
                                  area,
                                  configuration,
                                  setSelectedAreaId,
                                  setAreaName,
                                  setCenterAddress,
                                  setSoftRadiusMiles,
                                  setHardRadiusMiles,
                                  setFeeType,
                                  setFeeAmount,
                                  setIsDefault,
                                  setIsEditingName
                                )
                              }}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              <span className="sr-only">Edit area</span>
                            </Button>
                          </div>
                        )}
                        {isExpanded ? renderAreaEditor(true) : null}
                      </Card>
                    )
                  })}
              </div>
            </ScrollArea>
          </AppWorkspacePane>

          <AppWorkspacePane
            surface="muted"
            minHeight
            className="grid grid-rows-[auto_minmax(0,1fr)]"
          >
            <AppWorkspacePaneHeader
              eyebrow="Coverage map"
              description={displayAreaName}
              actions={
                <div className="shrink-0 rounded-[4px] border border-border/70 bg-background/70 px-2.5 py-1 text-xs text-muted-foreground">
                  Boundary map
                </div>
              }
            />
            <div className="min-h-0">
              <Suspense
                fallback={<div className={PREVIEW.map.loadingInset}>Loading map preview…</div>}
              >
                <ServiceAreaMap
                  policies={mapAreas}
                  selectedPolicyId={mapPreviewArea?.id ?? selectedAreaId}
                  draftPolicy={isCreatingNewArea ? previewDraftArea : null}
                  places={configuration.locations}
                  defaultCenter={previewCenter}
                  className="h-full rounded-none border-0"
                  viewportSignal={viewportSignal}
                  emptyStateLabel={emptyMapMessage}
                  onTogglePostalCode={selectedAreaId ? handleTogglePostalCode : undefined}
                />
              </Suspense>
            </div>
          </AppWorkspacePane>
        </AppWorkspaceSidebarDetailSplit>
      </AppWorkspacePanel>
    </>
  )
}
